// Shared WebSocket protocol types. Imported by backend and frontend.

export interface UserMeta {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  email_verified: boolean;
  /** Has this user ever completed (or explicitly skipped) the getting-started
   * modal? Drives whether AppShell redirects a fresh signup to /keys instead
   * of /dashboard. Backed by user_extra.onboarding_completed_at — see
   * backend/ws/handlers/onboarding.ts. */
  onboarding_completed: boolean;
}

export interface OrgMeta {
  id: string;
  name: string;
  slug: string;
  role: "owner" | "admin" | "member" | "super_admin";
  /** Whether this org may have 'super_admin' members (regular orgs can't). */
  is_super: boolean;
}

/** Org summary for the super-admin Organizations page — cross-org, not scoped to the caller's membership. */
export interface AdminOrgMeta {
  id: string;
  name: string;
  slug: string;
  is_super: boolean;
  member_count: number;
  created_at: number;
}

/** Every known feature flag, defined here so the set of flags is fixed at
 * build time (Repo.setFeatureFlag can only toggle one of these keys, never
 * invent a new one — see its own doc). Add a new page/capability behind a
 * flag by adding an entry here + a matching row in schema.ts/a migration. */
export const FEATURE_FLAG_DEFS = [
  {
    key: "integrations_page",
    label: "Integrations page",
    description:
      "Shows the Integrations page (GitHub App install) in the sidebar for every org. Off by default — no GitHub App is configured on this deployment yet. A super_admin can always see the page regardless, to preview/test it.",
  },
] as const;
export type FeatureFlagKey = (typeof FEATURE_FLAG_DEFS)[number]["key"];

/** A flag's live state, for the Feature Flags admin page (super_admin only). */
export interface FeatureFlagMeta {
  key: FeatureFlagKey;
  enabled: boolean;
  updated_at: number;
  updated_by: string | null;
}

/** API-key permission scopes the compile API recognises (see api auth/apikey.ts). */
export const API_KEY_SCOPES = [
  {
    value: "compile:submit",
    label: "Submit builds",
    description: "Compile projects via `pure build`",
  },
  {
    value: "compile:conformance",
    label: "Conformance runs",
    description: "Submit conformance / test compiles",
  },
  {
    value: "source:upload",
    label: "Upload compiler source",
    description: "Upload a custom compiler tarball",
  },
] as const;

export type Scope = (typeof API_KEY_SCOPES)[number]["value"];

/** Of the API-key scopes above, the ones NOT granted by default — a key can
 * always carry compile:submit, but compile:conformance and source:upload
 * are only usable in a super organization, and only by a member whose
 * custom role explicitly grants them (see CUSTOM_ROLE_SCOPES below). */
export const GATED_API_KEY_SCOPES = [
  "compile:conformance",
  "source:upload",
] as const satisfies readonly Scope[];

/** App-permission scopes assignable to a custom role. These aren't wired
 * into every handler yet (today's owner/admin/member/super_admin rank
 * checks are unaffected) — they exist so a custom role can be defined with
 * them now, ahead of individual handlers adopting fine-grained checks. */
export const APP_PERMISSION_SCOPES = [
  {
    value: "members:manage",
    label: "Manage members",
    description: "Invite, remove, and change the role of org members",
  },
  {
    value: "invites:manage",
    label: "Manage invites",
    description: "Send and revoke org invites",
  },
  {
    value: "keys:manage",
    label: "Manage API keys",
    description: "Create, rotate, revoke, and edit scopes on org API keys",
  },
  {
    value: "credentials:manage",
    label: "Manage credentials",
    description: "Create and revoke OIDC credentials",
  },
  {
    value: "org:settings",
    label: "Manage org settings",
    description: "Change organization name, slug, and linked accounts",
  },
  // Unlike the others above, this one IS wired up outside this repo: it
  // rides the management-audience SSO JWT (see sso.ts's resolveAndIssue)
  // into pure-auth-broker, which checks for it on join-request/box/
  // component approval endpoints (see that repo's joinRoutes.ts /
  // depRoutes.ts requireOperator) as an alternative to the owner/admin/
  // super_admin role check.
  {
    value: "ops:approve",
    label: "Approve boxes & components",
    description:
      "Approve join requests, onboarding boxes, and box components in the management console",
  },
] as const;

export type AppPermissionScope =
  (typeof APP_PERMISSION_SCOPES)[number]["value"];

/** Every scope a custom role can be given: the app-permission scopes plus
 * the gated API-key scopes. compile:submit is deliberately excluded — it's
 * baseline for everyone, never something a custom role needs to grant. */
export type CustomRoleScope =
  AppPermissionScope | (typeof GATED_API_KEY_SCOPES)[number];

export const CUSTOM_ROLE_SCOPES: {
  value: CustomRoleScope;
  label: string;
  description: string;
}[] = [
  ...APP_PERMISSION_SCOPES,
  ...API_KEY_SCOPES.filter(
    (
      s,
    ): s is (typeof API_KEY_SCOPES)[number] & {
      value: (typeof GATED_API_KEY_SCOPES)[number];
    } => (GATED_API_KEY_SCOPES as readonly string[]).includes(s.value),
  ),
];

export interface CustomRoleMeta {
  id: string;
  name: string;
  scopes: CustomRoleScope[];
  created_at: number;
}

export interface ApiKeyMeta {
  id: string;
  name: string;
  prefix: string;
  scopes: Scope[];
  created_at: number;
  created_by: string;
  last_used: number | null;
  expires_at: number | null;
  revoked_at: number | null;
}

export interface CredentialMeta {
  id: string;
  name: string;
  provider: "github" | "gitlab" | "aws" | "google";
  oidc_issuer: string;
  subject_claim: string;
  /** The user who created this connection — ADDED 2026-09-03. Uniqueness
   *  (see migrations/0009) and OIDC auth lookup (internal/api's
   *  gitlabOidc.ts) are both scoped to this user, not the org, so a CI
   *  pipeline authenticating against this connection needs BOTH this id
   *  (--account / $PURE_ACCOUNT_ID) and the org id it should bill to
   *  (--org / $PURE_ORG_ID) — shown per-row so an admin can read off the
   *  exact values a connection's owner needs to put in their pipeline. */
  created_by: string;
  created_at: number;
  last_used: number | null;
  revoked_at: number | null;
}

export interface MemberMeta {
  user_id: string;
  email: string;
  name: string | null;
  image: string | null;
  role: "owner" | "admin" | "member" | "super_admin";
  /** Extra scopes granted on top of `role`, via a custom role (super orgs only). */
  custom_role: { id: string; name: string } | null;
  joined_at: number;
}

export interface InviteMeta {
  id: string;
  email: string;
  role: "admin" | "member";
  expires_at: number;
  invited_by: string;
}

export interface JobMeta {
  id: string;
  status: "queued" | "running" | "succeeded" | "failed";
  source_kind: "tarball" | "zip" | "github" | "gitlab";
  source_summary: string;
  entry: string;
  credential_id: string | null;
  api_key_id: string | null;
  /** The API key's own human-readable name (api_keys.name) — lets a user
   *  with multiple keys tell which one submitted a given job. Null if the
   *  key has since been deleted (jobs.api_key_id has no FK to api_keys). */
  api_key_name: string | null;
  /** Who submitted the job (the API key's owning user, api_keys.created_by)
   *  — null if that user or the key itself is no longer resolvable. Only
   *  meaningful to 'admin'/'owner'/'super_admin' viewers browsing org-wide;
   *  a 'member' only ever sees their own jobs in the first place. */
  submitted_by: { user_id: string; name: string | null; email: string } | null;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  binary_bytes: number | null;
  binary_sha256: string | null;
  /** Code metrics (internal/compiler plans/2026-10-12-code-metrics.md;
   *  internal/api migrations/0014_jobs_code_metrics.sql). Null on jobs
   *  built before they existed, and on failed builds. Work units are
   *  internal-only and never part of JobMeta. */
  /** Statements/declarations in every JS/TS file the build loaded (entry +
   *  dependencies), before tree shaking. */
  total_lines_of_code: number | null;
  /** Lines after tree shaking, plus constant eval / new Function code
   *  compiled ahead of time. */
  compiled_lines_of_code: number | null;
  /** True when the binary includes the interpreter because the program
   *  needs dynamic code (eval, new Function, vm, dynamic require). */
  dynamic_code: boolean | null;
}

// ─── Client → Server ───

export type ClientMessage =
  | { type: "auth.token"; token: string }
  | { type: "org.select"; org_id: string }
  | { type: "onboarding.complete" }
  | { type: "keys.list" }
  | { type: "keys.create"; name: string; scopes?: Scope[]; expires_at?: number }
  // `pure login`'s browser-based flow (CliLoginPage) — deliberately NOT
  // `keys.create` (which requireRole('admin')s): any authenticated org
  // member can self-serve a personal CLI key this way, same reasoning
  // Claude Code's own /login lets any signed-in user mint a session —
  // scope is always the compile:submit baseline, never admin-gated
  // scopes. `state` round-trips the CLI's own nonce so the reply can be
  // matched to the right pending local-callback-server request if a
  // user somehow has two `pure login` flows open in different tabs at
  // once. `name` — ADDED 2026-09-03, direct operator directive: the CLI
  // now prompts the user for this itself (defaulting to
  // "CLI Login - <hostname> - <date>") before ever opening the browser,
  // instead of the server always stamping the same date-only name that
  // couldn't distinguish two devices in the dashboard's key list. Still
  // length-capped the same as keys.create's own `name`, and the handler
  // still has a server-side fallback for the (untrusted, URL-editable)
  // case this arrives blank.
  | { type: "keys.createForCli"; state: string; name?: string }
  | { type: "keys.rotate"; id: string }
  | { type: "keys.revoke"; id: string }
  | { type: "keys.delete"; id: string }
  | { type: "keys.update"; id: string; scopes: Scope[] }
  | { type: "creds.list" }
  | {
      type: "creds.create";
      name: string;
      provider: CredentialMeta["provider"];
      oidc_issuer: string;
      subject_claim: string;
    }
  | { type: "creds.revoke"; id: string }
  | { type: "members.list" }
  | { type: "members.invite"; email: string; role: "admin" | "member" }
  | { type: "members.remove"; user_id: string }
  | {
      type: "members.role";
      user_id: string;
      role: "owner" | "admin" | "member" | "super_admin";
    }
  | {
      type: "members.setCustomRole";
      user_id: string;
      custom_role_id: string | null;
    }
  | { type: "roles.list" }
  | { type: "roles.create"; name: string; scopes: CustomRoleScope[] }
  | { type: "roles.delete"; id: string }
  | { type: "flags.list" }
  | { type: "flags.set"; key: FeatureFlagKey; enabled: boolean }
  | { type: "invites.list" }
  | { type: "invites.revoke"; id: string }
  | { type: "jobs.list"; cursor?: string; filter?: Record<string, unknown> }
  | { type: "jobs.get"; job_id: string }
  | { type: "jobs.download"; job_id: string }
  | { type: "jobs.subscribe"; job_id: string }
  | { type: "jobs.unsubscribe"; job_id: string };

// ─── Server → Client ───

export type ServerMessage =
  | {
      type: "auth.ok";
      user: UserMeta;
      org: OrgMeta;
      orgs: OrgMeta[];
      flags: Record<string, boolean>;
    }
  | { type: "auth.error"; message: string }
  | { type: "org.switched"; org: OrgMeta }
  | { type: "onboarding.completed" }
  | { type: "keys.state"; keys: ApiKeyMeta[]; grantedGatedScopes: Scope[] }
  | { type: "keys.created"; key: ApiKeyMeta; raw: string }
  | { type: "keys.createdForCli"; state: string; key: ApiKeyMeta; raw: string }
  | { type: "keys.rotated"; old_id: string; key: ApiKeyMeta; raw: string }
  | { type: "keys.revoked"; id: string }
  | { type: "keys.deleted"; id: string }
  | { type: "keys.updated"; key: ApiKeyMeta }
  | { type: "keys.used"; id: string; ts: number }
  | { type: "creds.state"; credentials: CredentialMeta[] }
  | { type: "creds.created"; credential: CredentialMeta }
  | { type: "creds.revoked"; id: string }
  | { type: "members.state"; members: MemberMeta[] }
  | { type: "invites.state"; invites: InviteMeta[] }
  | { type: "invites.revoked"; id: string }
  | { type: "roles.state"; roles: CustomRoleMeta[] }
  | { type: "roles.deleted"; id: string }
  | { type: "flags.state"; flags: FeatureFlagMeta[] }
  | { type: "jobs.page"; jobs: JobMeta[]; cursor: string | null }
  | { type: "jobs.job"; job: JobMeta }
  | {
      type: "jobs.downloadUrl";
      job_id: string;
      url: string;
      expires_at: number;
    }
  | { type: "job.log"; job_id: string; line: string }
  | { type: "job.status"; job_id: string; status: JobMeta["status"] }
  | {
      type: "job.done";
      job_id: string;
      download_token: string;
      expires_at: number;
    }
  | { type: "error"; req_type: string; message: string };

export type MessageType<T extends { type: string }> = T["type"];
export type MessageOfType<
  T extends { type: string },
  K extends T["type"],
> = Extract<T, { type: K }>;
